/**
 * useCalendarRange (Plan 03-06).
 *
 * TanStack Query wrapper for GET /api/calendar/events.
 * Computes from/to range from view + anchor date, uses Monday-start
 * week boundaries via the project's locale-aware formatters.
 *
 * Per D-04: server returns fully-expanded EventInstance[] for [from, to).
 * The 6-week month window is ≤ 42 days, well inside the server's 90-day cap.
 *
 * The EventInstance type is inlined here (matching D-04 exactly).
 * Future: when @stell/shared exports it, swap for the import.
 */
import { useQuery } from '@tanstack/react-query';
import { addDays, endOfMonth, startOfMonth } from 'date-fns';
import { startOfWeek as osloStartOfWeek, endOfWeek as osloEndOfWeek } from '../../i18n/formatters';
import { CAL_TIMINGS } from './timings';

/** D-04 EventInstance shape (server-expanded per-occurrence record). */
export type EventInstance = {
  id: string;
  series_id: string;
  occurrence_start: string;
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
  title: string;
  description: string | null;
  location: string | null;
  color_token: string;
  attendee_ids: string[];
  is_override: boolean;
  override_kind: 'moved' | 'cancelled' | 'single' | null;
  /**
   * #165: seriens RRULE og skaper, til redigeringsarket. Valgfrie fordi et
   * bufret svar fra før #165 mangler dem; da vises hendelsen som før.
   */
  recurrence_rule?: string | null;
  created_by?: string;
};

export type CalendarView = 'month' | 'week' | 'day';

/**
 * Compute the fetch range (from, to) for a given view and anchor date.
 * Monday-anchored per D-14 / FND-08.
 */
export function rangeFor(view: CalendarView, anchor: Date): { from: Date; to: Date } {
  if (view === 'day') {
    const from = new Date(anchor);
    from.setHours(0, 0, 0, 0);
    const to = new Date(from);
    to.setDate(to.getDate() + 1);
    return { from, to };
  }
  if (view === 'week') {
    const from = osloStartOfWeek(anchor);
    const to = addDays(osloEndOfWeek(anchor), 1);
    return { from, to };
  }
  // month: Monday-anchored 6-week window covering the whole visible month
  const monthStart = startOfMonth(anchor);
  const monthEnd = endOfMonth(anchor);
  const from = osloStartOfWeek(monthStart);
  const to = addDays(osloEndOfWeek(monthEnd), 1);
  return { from, to };
}

interface UseCalendarRangeOptions {
  memberId?: string;
}

export function useCalendarRange(
  view: CalendarView,
  anchor: Date,
  opts: UseCalendarRangeOptions = {},
) {
  const { from, to } = rangeFor(view, anchor);

  return useQuery({
    queryKey: [
      'calendar',
      'events',
      view,
      from.toISOString(),
      to.toISOString(),
      opts.memberId ?? null,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        from: from.toISOString(),
        to: to.toISOString(),
      });
      if (opts.memberId) {
        params.set('member_id', opts.memberId);
      }
      const res = await fetch(`/api/calendar/events?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`calendar fetch ${res.status}`);
      }
      const json = (await res.json()) as { events: EventInstance[] };
      return json.events;
    },
    staleTime: CAL_TIMINGS.todayRefetchInterval / 2, // 15 minutes
  });
}
